import React, { useState, useEffect } from "react";
import axios from "axios";
import Swal from "sweetalert2";
import withReactContent from "sweetalert2-react-content";
import { useAuth } from "../../core/context/AuthContext";

const StockManagement = () => {
    const { user } = useAuth();
    const MySwal = withReactContent(Swal);
    const [products, setProducts] = useState([]);
    const [categories, setCategories] = useState([]);

    const [showInModal, setShowInModal] = useState(false);
    const [selectedVariant, setSelectedVariant] = useState(null);
    const [inData, setInData] = useState({ quantity: "", unitCost: "", notes: "" });

    // Search and Filter State
    const [searchQuery, setSearchQuery] = useState("");
    const [selectedCategory, setSelectedCategory] = useState("All");

    useEffect(() => {
        fetchData();
        fetchCategories();
    }, []);

    const fetchCategories = async () => {
        try {
            const res = await axios.get("https://avastar-pos-backend.onrender.com/api/categories/active");
            setCategories(res.data.filter(c => c.type !== "Menu"));
        } catch (error) {
            console.error(error);
        }
    };

    const fetchData = async () => {
        try {
            const res = await axios.get("https://avastar-pos-backend.onrender.com/api/inventory/products");
            setProducts(res.data);

        } catch (error) {
            console.error(error);

        }
    };

    const handleStockIn = async () => {
        const qty = Number(inData.quantity);
        const cost = Number(inData.unitCost);
        if (!Number.isFinite(qty) || qty <= 0) {
            return MySwal.fire("Error", "Enter a positive quantity", "error");
        }
        if (!Number.isFinite(cost) || cost < 0) {
            return MySwal.fire("Error", "Unit cost cannot be negative", "error");
        }
        try {
            const payload = {
                variantId: selectedVariant._id,
                quantity: qty,
                unitCost: cost || selectedVariant.costPerUnit,
                notes: inData.notes,
                ...(user?._id ? { user: user._id } : {}),
            };
            await axios.post("https://avastar-pos-backend.onrender.com/api/inventory/stock-in", payload);
            MySwal.fire("Success", "Stock Added!", "success");
            setShowInModal(false);
            fetchData();
        } catch (error) {
            MySwal.fire("Error", "Failed to add stock", "error");
        }
    };



    return (
        <div className="page-wrapper bg-light bg-opacity-50">
            <div className="content">
                <div className="page-header d-flex flex-column flex-md-row justify-content-between align-items-md-center mb-4 gap-3">
                    <div>
                        <h4 className="fw-bold mb-1">Stock Management</h4>
                        <p className="text-muted mb-0">Procure new supplies and receive stock for existing variants</p>
                    </div>
                </div>

                {/* Filters */}
                <div className="card shadow-sm border-0 mb-4">
                    <div className="card-body py-3 d-flex flex-column flex-md-row flex-wrap gap-3 align-items-stretch align-items-md-center">
                        <div className="input-group" style={{ flex: '1 1 300px' }}>
                            <span className="input-group-text bg-white border-end-0"><i className="fas fa-search text-muted"></i></span>
                            <input 
                                type="text" 
                                className="form-control border-start-0 ps-0" 
                                placeholder="Search by product, SKU, or category..." 
                                value={searchQuery}
                                onChange={(e) => setSearchQuery(e.target.value)}
                            />
                        </div>
                        <div style={{ flex: '1 1 200px' }}>
                            <select 
                                className="form-select" 
                                value={selectedCategory} 
                                onChange={(e) => setSelectedCategory(e.target.value)}
                            >
                                <option value="All">All Categories</option>
                                {categories.map(c => (
                                    <option key={c._id} value={c._id}>{c.type} - {c.name}</option>
                                ))}
                            </select>
                        </div>
                    </div>
                </div>

                {/* Products Grid */}
                <div className="row">
                    {products.filter(p => selectedCategory === "All" || p.category?._id === selectedCategory).filter(p => {
                        const s = searchQuery.toLowerCase();
                        if (!s) return true;
                        return p.name?.toLowerCase().includes(s) || p.variants?.some(v => v.name?.toLowerCase().includes(s));
                    }).map(product => (
                        <div key={product._id} className="col-12 mb-4">
                            <div className="card shadow-sm border-0 border-top border-primary border-3">
                                <div className="card-header bg-white border-bottom-0 pt-4 pb-0">
                                    <h5 className="fw-bold mb-0 text-dark"><i className="fas fa-boxes text-muted me-2"></i>{product.name}</h5>
                                    <p className="text-muted small mb-3">{product.category?.name} | {product.category?.type}</p>
                                </div>
                                <div className="card-body pt-2">
                                    {product.variants?.length === 0 ? (
                                        <p className="text-muted small">No batches/variants registered.</p>
                                    ) : (
                                        <div className="row g-3">
                                            {product.variants.map(v => (
                                                <div className="col-12 col-md-6 col-lg-4 col-xl-3" key={v._id}>
                                                    <div className="border rounded-3 p-3 bg-light h-100 position-relative shadow-sm" style={{ borderColor: v.currentStock <= v.reorderLevel ? '#fca5a5' : '#e2e8f0' }}>
                                                        {v.currentStock <= v.reorderLevel && (
                                                            <span className="position-absolute top-0 start-100 translate-middle p-2 bg-danger border border-light rounded-circle" title="Low Stock Warning">
                                                              <span className="visually-hidden">New alerts</span>
                                                            </span>
                                                        )}
                                                        <h6 className="fw-bold text-dark lh-sm mb-2">{v.name}</h6>
                                                        <div className="d-flex justify-content-between text-muted small mb-3">
                                                            <span>Cost: <span className="text-dark fw-bold">Rs {v.costPerUnit}</span></span>
                                                            <span className={`fw-bold px-2 py-1 rounded ${v.currentStock <= v.reorderLevel ? 'bg-danger text-white' : 'bg-success text-white'}`}>
                                                                {v.currentStock} {v.unit}
                                                            </span>
                                                        </div>
                                                        <button className="btn btn-sm btn-outline-primary w-100 fw-bold border-2" onClick={() => {
                                                            setSelectedVariant({ ...v, productName: product.name });
                                                            setInData({ quantity: "", unitCost: v.costPerUnit, notes: `Stock In ${new Date().toLocaleDateString()}` });
                                                            setShowInModal(true);
                                                        }}>
                                                            <i className="fas fa-arrow-down me-1"></i> Receive Stock
                                                        </button>
                                                    </div>
                                                </div>
                                            ))}
                                        </div>
                                    )}
                                </div>
                            </div>
                        </div>
                    ))}
                    {products.length === 0 && (
                        <div className="text-center w-100 py-5 text-muted">No products found.</div>
                    )}
                </div>
            </div>

            {/* Stock In Modal */}
            {showInModal && (
                <div className="modal show d-block" style={{ background: "rgba(0,0,0,0.5)" }}>
                    <div className="modal-dialog modal-dialog-centered">
                        <div className="modal-content">
                            <div className="modal-header bg-primary text-white">
                                <h5 className="modal-title"><i className="fas fa-box-open me-2"></i> Receive Stock</h5>
                                <button type="button" className="btn-close btn-close-white" onClick={() => setShowInModal(false)}></button>
                            </div>
                            <div className="modal-body">
                                <h6><strong>Item:</strong> {selectedVariant?.productName} - {selectedVariant?.name}</h6>
                                <p className="text-muted small mb-4">Current Physical Stock: {selectedVariant?.currentStock} {selectedVariant?.unit}</p>

                                <div className="mb-3">
                                    <label className="form-label">Quantity Received ({selectedVariant?.unit}) <span className="text-danger">*</span></label>
                                    <input type="number" className="form-control form-control-lg border-primary" value={inData.quantity} onChange={e => setInData({ ...inData, quantity: e.target.value })} autoFocus />
                                </div>
                                <div className="mb-3">
                                    <label className="form-label">Unit Cost Purchase Price (Rs)</label>
                                    <input type="number" min="0" step="0.01" className="form-control" value={inData.unitCost} onChange={e => setInData({ ...inData, unitCost: e.target.value })} />
                                    <small className="text-muted">Updates the global cost-per-unit for this variant automatically.</small>
                                </div>
                                <div className="mb-3">
                                    <label className="form-label">Note/Reference</label>
                                    <input type="text" className="form-control" value={inData.notes} onChange={e => setInData({ ...inData, notes: e.target.value })} placeholder="e.g. Invoice #10243" />
                                </div>
                            </div>
                            <div className="modal-footer pb-4 pe-4">
                                <button className="btn btn-light px-4 py-2" onClick={() => setShowInModal(false)}>Cancel</button>
                                <button className="btn btn-primary fw-bold px-4 py-2 shadow" onClick={handleStockIn}>
                                    Confirm <i className="fas fa-check ms-1"></i>
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};
export default StockManagement;
